import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { swarmCommand } from 'services/get-swarm-data';
import {
  setGeoComplyErrorPopupAvailable,
  setGeoComplyErrorPopupText
} from 'store/actions';
import { getUser } from 'store/selectors/user-data';

export const UseCheckGeoComply = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector(getUser);

  const checkLocation = () => {
    return new Promise((resolve, reject) => {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        if (result.state === 'granted') {
          onGeoLocationSuccess()
            .then(license => {
              resolve(license);
            })
            .catch(() => {
              reject();
            });
        } else if (result.state === 'prompt') {
          navigator.geolocation.getCurrentPosition(
            () => {
              onGeoLocationSuccess()
                .then(license => {
                  resolve(license);
                })
                .catch(() => {
                  reject();
                });
            },
            () => {
              reject(t('account.allowLocation'));
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
          );
        } else if (result.state === 'denied') {
          reject(t('account.allowLocation'));
        }
      });
    });
  };

  const onGeoLocationSuccess = async () => {
    const licenseString = await getGeoComplyLicenseString();

    return new Promise((resolve, reject) => {
      licenseString ? resolve(licenseString) : reject();
    });
  };

  const getGeoComplyLicenseString = async () => {
    return new Promise((resolve, reject) => {
      swarmCommand(
        CommandNames.GET_GEO_COMPLY_LICENSE_STRING,
        {},
        (data: {
          result: number;
          details: {
            LicenseString: string;
          };
        }) => {
          if (data.result === 0 && data.details.LicenseString) {
            resolve(data.details.LicenseString);
          } else {
            reject();
          }
        },
        null
      );
    });
  };

  const geoComplyLocationCheck = () => {
    return new Promise<void>((resolve, reject) => {
      checkActionAllowed('check')
        .then(licenseString => {
          swarmCommand(
            CommandNames.GEO_COMPLY_GEOLOCATION_CHECK,
            { location_token: licenseString },
            (data: {
              result: number;
              details: {
                IsSuccess: boolean;
                ErrorCode: number;
                ErrorMessage: null | string;
              };
            }) => {
              if (data.result === 0 && data.details.IsSuccess) {
                resolve();
              }
            },
            null,
            () => {
              dispatch(setGeoComplyErrorPopupAvailable(false));
              dispatch(setGeoComplyErrorPopupText(null));
              reject();
            }
          );
        })
        .catch(e => {
          dispatch(setGeoComplyErrorPopupAvailable(true));
          dispatch(setGeoComplyErrorPopupText(e));
          reject(e);
        });
    });
  };

  const checkActionAllowed = useCallback(
    (
      reason: string,
      username?: string
    ): Promise<string | { code: string | number; message: string }> => {
      const userName = username || user.username || user.email || user.phone;

      return new Promise((resolve, reject) => {
        checkLocation()
          .then(license => {
            const geoClient = window?.GcHtml5.createClient();
            geoClient.events
              .on('success', function (gcResponse: string) {
                resolve(gcResponse);
              })
              .on('failed', function (code: string | number, message: string) {
                reject({ code: code, message: message });
              });
            geoClient.setLicense(`${license}`);
            geoClient.setUserId(userName || '');
            geoClient.setReason(reason);
            geoClient.request({
              acceptNoCoordinates: true
            });
          })
          .catch(e => {
            if (e) {
              dispatch(setGeoComplyErrorPopupAvailable(true));
              dispatch(setGeoComplyErrorPopupText(e));
              e && reject(e);
            }
          });
      });
    },
    [user.username, user.email, user.phone]
  );

  return {
    checkActionAllowed: checkActionAllowed,
    geoComplyLocationCheck: geoComplyLocationCheck
  };
};
